import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Box,
    Grid,
    Link
} from '@mui/material';
import ZiphyPlaceInput from '../book/ZiphyPlaceInput';
import { Place } from '../../models/ziphy';
import { Client, Patient } from '../../models/core';
import { Insurance } from '../../models/insurance';
import InsuranceList from './2_0/insurance/InsuranceList';
import AddressDialog, { AddressDialogMode } from './2_0/schedule/AddressDialog';

interface ZiphyPatientConfirmDialogProps {
    open: boolean;
    patient:Patient;
    client:Client;
    insurance:Insurance[];
    onClose: () => void;
    onConfirm: (patient:Patient, insurance:Insurance[], place:Place|null) => Promise<void>;
}

const ZiphyPatientConfirmDialog: React.FC<ZiphyPatientConfirmDialogProps> = ({
    open,
    patient,
    insurance,
    client,
    onClose,
    onConfirm,
}) => {
    const [isCreating, setIsCreating] = React.useState<boolean>(false);
    const [placeValue, setPlaceValue] = React.useState<Place | null>(null);
    const [showPlaceEditor, setShowPlaceEditor] = React.useState(false);

    const practiceId = React.useMemo(() => { return client.practiceId}, [client]);

    const handlePlaceChange = (place: Place | null) => {
        setPlaceValue(place);
    };

    const handleConfirm = async () => {
        setIsCreating(true);
        await onConfirm(patient, insurance, placeValue);
        setIsCreating(false);
    };

    const pad = (n: number) => n.toString().padStart(2, '0');
    const getDOB = (dobString:string) => {
        const date = new Date(dobString||'');
        return `${pad(date.getMonth()+1)}/${pad(date.getDate())}/${date.getFullYear()}`
    }

    return (
        <>
        <Dialog open={open} onClose={onClose} fullWidth
            PaperProps={{sx:{ maxWidth: '45%', minWidth:'500px' }}}>
            <DialogTitle><b>{'Attention'}</b></DialogTitle>
                <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                        {`You are about to create a new Ziphy Medical Patient using Outreach Patient:`}
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                    <b>{`${patient?.lastName}, ${patient?.firstName}`}</b>.
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #e0e0e0', borderRadius: '8px',
                        p: 2, maxHeight: '60vh', overflowY: 'auto', margin:3
                    }}
                >
                    <Box sx={{ ml:1, mr:1, mt: 1, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Details</Typography>
                        <Grid container>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>DOB:</b> {getDOB(patient?.dob||'')}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Gender:</b> {patient?.gender}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Language:</b> {patient?.language}</Typography>
                            </Grid>
                            <Grid item xs={12}><Box height={'0.5em'}/></Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Phone:</b> {patient?.phone||''}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Alt Phone:</b> {patient?.phone2}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2"><b>Email:</b> {patient?.email}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mt: 1, mb: 3 }}>
                        <InsuranceList title='Insurance' readonly={true} controlled={true} insurance={insurance}/>
                    </Box>

                    <Box sx={{ mt: 3, ml: 1, mr: 1, mb: 3}}>
                        <Typography variant="h6" gutterBottom>Place</Typography>
                        <ZiphyPlaceInput
                            placeholder='Find Place'
                            practiceId={practiceId}
                            initialAddress={{id:'', line:[patient.address||''], city:patient.city, postalCode:patient.zip}}
                            value={placeValue}
                            onChange={handlePlaceChange}
                            disabled={isCreating}
                        />
                        <Typography variant='body2' sx={{ visibility: placeValue == null ? 'visible' : 'hidden' }}>
                            <Link component='button' variant='body2' onClick={()=>setShowPlaceEditor(true)}>Create New Place</Link>
                        </Typography>
                    </Box>
                </Box>
            <Grid container><Grid item xs={12}><Box height={'0.25em'}/></Grid></Grid>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                    {'This cannot be undone.'}
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                    {'Do you want to continue?'}
                </Typography>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={isCreating}
                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    Create Patient
                </Button>
            </DialogActions>
        </Dialog>
        {
            showPlaceEditor && <AddressDialog mode={AddressDialogMode.ChooseAddress} outreachPatient={patient}
                open={showPlaceEditor} practiceId={practiceId} onChange={(place) => {
                    handlePlaceChange(place);
                    setShowPlaceEditor(false);
                }
                }
                onClose={() => setShowPlaceEditor(false)}/>
        }
        </>
    );
};

export default ZiphyPatientConfirmDialog;